import {
  AuditorUpdateTaskCommandInput,
  AuditorUpdateTaskCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { taskKeys } from 'src/data/api/helios-service-ts/task/queries/keys';
import { taskService } from 'src/data/api/helios-service-ts/task/service';

/**
 * React Query mutation hook for updating an existing task draft via auditor update.
 * Uses raw HTTP call instead of the TypeScript client to avoid Smithy
 * deserialization errors caused by non-RFC-3339 date fields from the backend.
 * Invalidates the task list query on success to trigger a refetch.
 * No retry — mutation is not idempotent.
 *
 * TODO: Switch back to taskService.auditorUpdate (TypeScript client) once the backend
 * returns date fields in RFC-3339 format.
 *
 * @param key - Key parameters for mutation identification (businessUnitId, taskId)
 * @param options - Optional mutation options (mutationKey and mutationFn cannot be overridden)
 * @returns React Query mutation result
 */
export const useAuditorUpdateTaskMutation = (
  key: Pick<AuditorUpdateTaskCommandInput, 'businessUnitId' | 'taskId'>,
  options?: Omit<
    UseMutationOptions<
      AuditorUpdateTaskCommandOutput,
      Error,
      AuditorUpdateTaskCommandInput
    >,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationKey: taskKeys.auditorUpdate(key),
    mutationFn: (data: AuditorUpdateTaskCommandInput) =>
      taskService.auditorUpdateRaw(data),
    async onSuccess(data, variables, onMutateResult, context) {
      // Fire-and-forget: don't let a failed refetch corrupt the mutation success state
      queryClient.invalidateQueries({ queryKey: taskKeys.all }).catch(() => {});
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
